<!-- dashboard/routes/dashboard/settings/account/components/auto-resolve/ConfigSection.vue -->
<script setup>
import { ref, computed } from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ConfigCard from './ConfigCard.vue';
import ConfigForm from './ConfigForm.vue';

const props = defineProps({
  type: {
    type: String,
    required: true,
    validator: (v) => ['label', 'agent', 'team'].includes(v),
  },
  configs: {
    type: Map,
    required: true,
  },
  selectOptions: {
    type: Array,
    default: () => [],
  },
  labelOptions: {
    type: Array,
    default: () => [],
  },
  durationOptions: {
    type: Array,
    default: () => [],
  },
  isSubmitting: {
    type: Boolean,
    default: false,
  },
  formatDuration: {
    type: Function,
    required: true,
  },
  getConfigForEdit: {
    type: Function,
    required: true,
  },
});

const emit = defineEmits(['add', 'edit', 'remove']);

const showForm = ref(false);
const isEditing = ref(false);
const editingKey = ref(null);
const editingItem = ref(null);
const initialFormData = ref(null);

const sectionConfig = computed(() => {
  const configs = {
    label: {
      title: 'Por Etiqueta',
      description: 'Configurações aplicadas quando a conversa possui etiquetas específicas.',
      addLabel: 'Adicionar',
      emptyIcon: 'i-lucide-tag',
      emptyTitle: 'Nenhuma configuração',
      emptyDescription: 'Clique em Adicionar para criar uma regra por etiqueta',
    },
    agent: {
      title: 'Por Agente',
      description: 'Configurações aplicadas para conversas atribuídas a agentes específicos.',
      addLabel: 'Adicionar',
      emptyIcon: 'i-lucide-user',
      emptyTitle: 'Nenhuma configuração',
      emptyDescription: 'Clique em Adicionar para criar uma regra por agente',
    },
    team: {
      title: 'Por Time',
      description: 'Configurações aplicadas para conversas atribuídas a times específicos.',
      addLabel: 'Adicionar',
      emptyIcon: 'i-lucide-users',
      emptyTitle: 'Nenhuma configuração',
      emptyDescription: 'Clique em Adicionar para criar uma regra por time',
    },
  };
  return configs[props.type];
});

const hasConfigs = computed(() => props.configs.size > 0);
const canAdd = computed(() => props.selectOptions.length > 0);

const startAdding = () => {
  showForm.value = true;
  isEditing.value = false;
  editingKey.value = null;
  editingItem.value = null;
  initialFormData.value = null;
};

const startEditing = (key, config) => {
  const data = props.getConfigForEdit(props.type, key);
  if (!data) return;

  showForm.value = true;
  isEditing.value = true;
  editingKey.value = key;
  editingItem.value = { 
    name: config.name, 
    color: config.color,
    email: config.email,
    description: config.description,
  };
  initialFormData.value = data;
};

const cancelForm = () => {
  showForm.value = false;
  isEditing.value = false;
  editingKey.value = null;
  editingItem.value = null;
  initialFormData.value = null;
};

const handleSubmit = (formData) => {
  const key = isEditing.value ? editingKey.value : getKeyFromSelection(formData.selectedItem);
  
  if (!key && !isEditing.value) return;

  emit(isEditing.value ? 'edit' : 'add', {
    key,
    config: {
      name: formData.selectedItem?.name || editingItem.value?.name,
      color: formData.selectedItem?.color || editingItem.value?.color,
      duration: formData.duration,
      unit: formData.unit,
      message: formData.message,
      labelToApply: formData.labelToApply,
      ignoreWaiting: formData.ignoreWaiting,
    },
    onSuccess: cancelForm,
  });
};

const handleRemove = (key) => {
  emit('remove', { key });
};

const getKeyFromSelection = (item) => {
  if (!item) return null;
  return props.type === 'label' ? item.name : item.id;
};
</script>

<template>
  <div class="space-y-4">
    <div class="flex justify-between items-start gap-4">
      <div>
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ sectionConfig.title }}
        </h3>
        <p class="text-sm text-n-slate-10 mt-0.5">
          {{ sectionConfig.description }}
        </p>
      </div>
      <NextButton
        v-if="!showForm"
        slate
        xs
        @click="startAdding"
        :disabled="!canAdd"
        icon="i-lucide-plus"
        :label="sectionConfig.addLabel"
      />
    </div>

    <div v-if="hasConfigs" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      <ConfigCard
        v-for="[key, config] in configs.entries()"
        :key="key"
        :config="config"
        :type="type"
        :format-duration="formatDuration"
        @edit="startEditing(key, config)"
        @remove="handleRemove(key)"
      />
    </div>

    <div
      v-else-if="!showForm"
      class="flex flex-col items-center justify-center py-10 border border-dashed border-n-weak rounded-xl"
    >
      <span :class="[sectionConfig.emptyIcon, 'w-6 h-6 text-n-slate-8 mb-2']" />
      <p class="text-sm text-n-slate-10">
        {{ sectionConfig.emptyTitle }}
      </p>
      <p class="text-xs text-n-slate-9 mt-0.5">
        {{ sectionConfig.emptyDescription }}
      </p>
    </div>

    <ConfigForm
      v-if="showForm"
      :type="type"
      :is-editing="isEditing"
      :editing-item="editingItem"
      :initial-data="initialFormData"
      :select-options="selectOptions"
      :label-options="labelOptions"
      :duration-options="durationOptions"
      :is-submitting="isSubmitting"
      @submit="handleSubmit"
      @cancel="cancelForm"
    />
  </div>
</template>